import { useDisclosure } from "@chakra-ui/react";
import { CheckmarkFilledIcon } from "@looksrare/uikit";
import { useTranslation } from "next-i18next";
import { useGetFormattedErrorAndTitle, useSubmitTransaction } from "@looksrare/utils";
import { Button, ButtonProps } from "uikit";
import { useToast } from "hooks/useToast";
import { ConfirmInWalletModal } from "components/Modals/ConfirmInWalletModal";
import { Raffle } from "views/win/userGeneratedRaffles/types";
import { useClaimRaffleRefund } from "views/win/userGeneratedRaffles/hooks/contractHooks";

interface ClaimRefundButtonProps extends ButtonProps {
  raffleOnChainId: Raffle["raffleOnChainId"];
  onSuccess?: () => void;
}

export const ClaimRefundButton = ({ raffleOnChainId, onSuccess, children, ...props }: ClaimRefundButtonProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const claimRefund = useClaimRaffleRefund();
  const getFormattedErrorAndTitle = useGetFormattedErrorAndTitle();

  const handler = useSubmitTransaction({
    onSend: async () => {
      onOpen();
      const tx = await claimRefund(raffleOnChainId);
      return tx;
    },
    onSuccess: () => {
      onSuccess?.();
      toast({
        title: t("Refund claimed"),
      });
      onClose();
    },
    onError: (error: any) => {
      const { title, description } = getFormattedErrorAndTitle(error);
      toast({ title, description, status: "error", dataIdSuffix: "claim-refund" });
    },
  });

  return (
    <>
      <ConfirmInWalletModal
        isOpen={isOpen}
        onClose={onClose}
        isTxConfirmed={handler.isTxConfirmed}
        isTxSending={handler.isTxSending}
        isTxWaiting={handler.isTxWaiting}
        isTxError={handler.isTxError}
      />
      <Button
        size="xs"
        onClick={handler.submitTransaction}
        rightIcon={handler.isTxConfirmed ? <CheckmarkFilledIcon /> : undefined}
        isLoading={handler.isTxSending || handler.isTxWaiting}
        isDisabled={handler.isTxConfirmed || handler.isTxSending || handler.isTxWaiting}
        {...props}
      >
        {handler.isTxConfirmed ? t("Claimed") : children || t("Claim Refund")}
      </Button>
    </>
  );
};
